import React from 'react';
import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';
import { ComponentPage, DemoCard } from '../../shared';

const inDays = (n) => new Date(Date.now() + n * 24 * 60 * 60 * 1000).toISOString();

export const meta = {
  component: FomoBlock,
  componentName: 'FomoBlock',
  title: 'Fomo Block',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/FomoBlock',
  description:
    'Countdown strip above a school shop grid. Given closesAt it recomputes every minute and renders itself away once the deadline passes — callers never need to gate it. staticTime swaps the ticker for a fixed date while keeping that disappearing behaviour.',
  props: [
    ['text', 'string', 'Danish default', 'Static copy, used when closesAt is not given'],
    ['showIcon', 'bool', 'true', 'Show the clock icon'],
    ['closesAt', 'string', 'null', 'ISO date or datetime. Drives the countdown and gates the whole block.'],
    ['template', 'string', "'Skynd dig! Webshoppen lukker om {time}'", 'Copy with a {time} placeholder, used only with closesAt'],
    ['units', 'object', "{ day: 'd', hour: 't', minute: 'm' }", 'Localized day / hour / minute suffixes'],
    ['staticTime', 'string', 'null', 'Pre-formatted date substituted for {time} instead of the ticking countdown'],
  ],
  propsNote:
    'This component declares no propTypes, so the props-drift test cannot verify this table — keep it in step with the component by hand.',
  usage: [
    {
      title: 'Counting down to a shop close',
      code: `import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';

// Renders nothing once the deadline passes — no conditional needed
<FomoBlock
  closesAt={shop.endsAt}
  template="Skynd dig! Webshoppen lukker om {time}"
  units={{ day: 'd', hour: 't', minute: 'm' }}
/>`,
    },
    {
      title: 'Close date instead of a ticker',
      code: `// staticTime replaces {time}; closesAt still hides the block afterwards
<FomoBlock
  closesAt={shop.endsAt}
  staticTime={formatDateDK(shop.endsAt)}
  template="Webshoppen lukker {time}"
/>`,
    },
  ],
};

export default function FomoBlockPage() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Counting down" hint="recomputes every minute">
        <FomoBlock closesAt={inDays(12)} />
      </DemoCard>

      <DemoCard title="Close date" hint="staticTime — no ticker, still disappears after the deadline">
        <FomoBlock closesAt={inDays(12)} staticTime="22. oktober" template="Webshoppen lukker {time}" />
      </DemoCard>

      <DemoCard title="Static copy" hint="no closesAt">
        <FomoBlock text="Skynd dig! Webshoppen lukker snart" />
      </DemoCard>

      <DemoCard title="Without the icon">
        <FomoBlock text="Sidste chance for levering før jul" showIcon={false} />
      </DemoCard>

      <DemoCard title="Swedish units">
        <FomoBlock
          closesAt={inDays(3)}
          template="Skynda dig! Webshoppen stänger om {time}"
          units={{ day: 'd', hour: 'h', minute: 'm' }}
        />
      </DemoCard>

      <DemoCard title="Past deadline" hint="renders nothing at all" surface={false}>
        <FomoBlock closesAt={inDays(-1)} />
        <p className="typo-body" style={{ color: 'var(--ds-doc-ink-muted)' }}>
          The block above returned null — that is the whole demo.
        </p>
      </DemoCard>
    </ComponentPage>
  );
}
